import { useTranslation } from 'react-i18next';
import { Progress } from '@/components/ui/progress';
import { formatNumber, getCurrencySymbol } from '@/lib/format';
import { cn } from '@/lib/utils';

type MinimumOrderProgressProps = {
  currentAmount: number;
  minimumAmount: number;
  currency?: string;
  className?: string;
};

export function MinimumOrderProgress({
  currentAmount,
  minimumAmount,
  currency,
  className,
}: MinimumOrderProgressProps) {
  const { t, i18n } = useTranslation('lotSizing');
  const locale = i18n.language ?? 'en';
  const displayCurrency = ` ${getCurrencySymbol(currency)}`;
  const progressPercent =
    minimumAmount > 0 ? (currentAmount / minimumAmount) * 100 : 0;
  const displayPercent = Math.min(100, progressPercent);

  const indicatorColor =
    progressPercent >= 100
      ? '**:data-[slot=progress-indicator]:bg-green-500'
      : '**:data-[slot=progress-indicator]:bg-red-500';

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className='text-muted-foreground text-xs'>
        {t('minimumOrderAmountLabel')} :{' '}
        <span className='font-medium text-foreground'>
          {formatNumber(currentAmount, { locale })} /{' '}
          {formatNumber(minimumAmount, { locale })}
          {displayCurrency}
        </span>
      </div>
      <Progress
        aria-label={t('minimumOrderAmountAriaLabel', {
          current: formatNumber(currentAmount, { locale }),
          minimum: formatNumber(minimumAmount, { locale }),
          currency: displayCurrency,
        })}
        className={cn('w-1/3 bg-muted', indicatorColor)}
        value={displayPercent}
      />
    </div>
  );
}
